import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import SchoolIcon from "@mui/icons-material/SchoolOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import Swal from "sweetalert2";
import StatusChip from "components/StatusChip";
import { API_BASE } from "config/api";

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const NameCell = ({ row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {row.original.firstName} {row.original.surname}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {row.original.phone || row.original.email || "No contact info"}
    </Typography>
  </Box>
);
NameCell.propTypes = { row: PropTypes.shape({ original: PropTypes.object }).isRequired };

const STATUS_LABELS = {
  enrolled: "Enrolled",
  in_progress: "In Progress",
  graduated: "Graduated",
  dropped: "Dropped",
};

const StatusCell = ({ value }) => <StatusChip value={STATUS_LABELS[value] || value} />;
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const SessionsCell = ({ value }) => (
  <Typography variant="button" fontWeight={600}>
    {value} session{value === 1 ? "" : "s"}
  </Typography>
);
SessionsCell.propTypes = { value: PropTypes.number.isRequired };

const ActionsCell = ({ row, handleEdit, handleDelete, handleSessions, handleGraduate }) => (
  <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap">
    <Button
      size="small"
      variant="outlined"
      color="info"
      startIcon={<SchoolIcon fontSize="small" />}
      onClick={() => handleSessions(row.original)}
    >
      Sessions
    </Button>
    {row.original.status !== "graduated" && (
      <Button
        size="small"
        variant="outlined"
        color="success"
        startIcon={<CheckCircleIcon fontSize="small" />}
        onClick={() => handleGraduate(row.original)}
      >
        Graduate
      </Button>
    )}
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Box>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleSessions: PropTypes.func.isRequired,
  handleGraduate: PropTypes.func.isRequired,
};

const emptyForm = () => ({
  id: null,
  first_name: "",
  surname: "",
  phone: "",
  email: "",
  address: "",
  conversion_date: new Date().toISOString().split("T")[0],
  notes: "",
  status: "enrolled",
});

export default function useNewConvertData() {
  const { church } = useAuth();

  const [convertData, setConvertData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "Convert", accessor: "firstName", align: "left", width: "20%", Cell: NameCell },
      {
        Header: "Conversion Date",
        accessor: "conversionDate",
        align: "center",
        width: "13%",
        Cell: SerialCell,
      },
      {
        Header: "Sessions",
        accessor: "sessionCount",
        align: "center",
        width: "12%",
        Cell: SessionsCell,
      },
      { Header: "Status", accessor: "status", align: "center", width: "10%", Cell: StatusCell },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "40%",
        Cell: (props) => (
          <ActionsCell
            {...props}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleSessions={handleSessions}
            handleGraduate={handleGraduate}
          />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sessionsModalOpen, setSessionsModalOpen] = useState(false);
  const [selectedConvert, setSelectedConvert] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [isSaving, setIsSaving] = useState(false);
  const [newSession, setNewSession] = useState({ session_date: "", topic: "", notes: "" });
  const [isSavingSession, setIsSavingSession] = useState(false);

  const fetchConverts = useCallback(async () => {
    if (!church?.id) return;
    setConvertData((prev) => ({ ...prev, isLoading: true }));
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const response = await fetch(`${API_BASE}/new-converts`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const converts = result.data || [];

      setConvertData((prev) => ({
        ...prev,
        rows: converts.map((c, index) => ({
          id: c.id,
          serial: index + 1,
          firstName: c.first_name,
          surname: c.surname,
          phone: c.phone,
          email: c.email,
          conversionDate: c.conversion_date ? new Date(c.conversion_date).toLocaleDateString() : "",
          sessionCount: (c.sessions || []).length,
          status: c.status,
          originalData: c,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch new converts failed:", error);
      setConvertData((prev) => ({ ...prev, isLoading: false, error: error.message }));
    }
  }, [church?.id]);

  useEffect(() => {
    fetchConverts();
  }, [fetchConverts]);

  const handleAdd = () => {
    setForm(emptyForm());
    setEditModalOpen(true);
  };

  const handleEdit = useCallback((convert) => {
    const raw = convert.originalData || {};
    setForm({
      id: convert.id,
      first_name: raw.first_name || "",
      surname: raw.surname || "",
      phone: raw.phone || "",
      email: raw.email || "",
      address: raw.address || "",
      conversion_date: raw.conversion_date ? raw.conversion_date.split("T")[0] : "",
      notes: raw.notes || "",
      status: raw.status || "enrolled",
    });
    setEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((convert) => {
    setSelectedConvert(convert);
    setDeleteModalOpen(true);
  }, []);

  const handleSessions = useCallback((convert) => {
    setSelectedConvert(convert);
    setNewSession({ session_date: new Date().toISOString().split("T")[0], topic: "", notes: "" });
    setSessionsModalOpen(true);
  }, []);

  const handleGraduate = useCallback(async (convert) => {
    const result = await Swal.fire({
      title: "Graduate to full Member?",
      text: `${convert.firstName} ${convert.surname} will be added to the Members list. You can finish their profile and send login credentials from the Members page afterward.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Graduate",
    });
    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const response = await fetch(`${API_BASE}/new-converts/${convert.id}/graduate`, {
        method: "POST",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }
      Swal.fire({
        title: "Success!",
        text: "Convert graduated to a full Member.",
        icon: "success",
      });
      fetchConverts();
    } catch (error) {
      console.error("Graduate failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  }, []);

  const handleSubmit = async () => {
    try {
      if (!form.first_name.trim() || !form.surname.trim()) {
        throw new Error("First name and surname are required.");
      }
      if (!form.conversion_date) {
        throw new Error("Pick a conversion date.");
      }

      setIsSaving(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = form.id ? `${API_BASE}/new-converts/${form.id}` : `${API_BASE}/new-converts`;
      const method = form.id ? "PUT" : "POST";

      const payload = { ...form };
      delete payload.id;
      Object.keys(payload).forEach((key) => {
        if (payload[key] === "") payload[key] = null;
      });

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `New convert ${form.id ? "updated" : "enrolled"}`,
        icon: "success",
      });
      fetchConverts();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Save new convert failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const response = await fetch(`${API_BASE}/new-converts/${selectedConvert.id}`, {
        method: "DELETE",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      Swal.fire({ title: "Success!", text: "New convert deleted", icon: "success" });
      fetchConverts();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete new convert failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  const handleAddSession = async () => {
    try {
      if (!newSession.session_date) throw new Error("Pick a session date.");

      setIsSavingSession(true);
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/new-converts/${selectedConvert.id}/sessions`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newSession),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      const body = await response.json();
      setSelectedConvert((prev) => ({
        ...prev,
        originalData: {
          ...prev.originalData,
          sessions: [body.data, ...(prev.originalData.sessions || [])],
        },
      }));
      setNewSession({ session_date: new Date().toISOString().split("T")[0], topic: "", notes: "" });
      fetchConverts();
    } catch (error) {
      console.error("Add session failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    } finally {
      setIsSavingSession(false);
    }
  };

  const handlePrint = () => window.print();

  return {
    ...convertData,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    sessionsModalOpen,
    setSessionsModalOpen,
    selectedConvert,
    form,
    setForm,
    handleAdd,
    handleSubmit,
    handleDeleteConfirm,
    isSaving,
    newSession,
    setNewSession,
    handleAddSession,
    isSavingSession,
  };
}
